import { motion } from 'framer-motion';
import {
  Users,
  Target,
  TrendingUp,
  BookOpen,
  Award,
  FileText,
  ArrowRight,
  Phone,
  CheckCircle2,
  Building,
  Rocket,
  Landmark,
  UserPlus,
  ClipboardList
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const services = [
  {
    icon: FileText,
    title: 'Business Process Analysis',
    description: 'Analisis mendalam proses bisnis untuk identifikasi kebutuhan SDM'
  },
  {
    icon: Users,
    title: 'Workforce Planning',
    description: 'Perencanaan jumlah, kompetensi, dan struktur tim yang optimal'
  },
  {
    icon: Target,
    title: 'Analisis Gap Kompetensi',
    description: 'Identifikasi kesenjangan skill dan strategi pengembangan SDM'
  },
  {
    icon: BookOpen,
    title: 'Desain Kurikulum Pelatihan',
    description: 'Program peningkatan kapasitas berbasis kebutuhan bisnis'
  },
  {
    icon: TrendingUp,
    title: 'Perencanaan Suksesi & Talent Development',
    description: 'Pengembangan jalur karir dan succession planning'
  },
  {
    icon: Award,
    title: 'Konsultasi Kebijakan HR & Organisasi',
    description: 'Penyusunan kebijakan adaptif terhadap perubahan'
  },
  {
    icon: ClipboardList,
    title: 'SOP & KPI Audit and Development',
    description: 'Merancang dan mengaudit SOP serta KPI yang jelas, terukur, dan selaras dengan target bisnis untuk meningkatkan performa tim secara sistematis.'
  }
];

const suitableFor = [
  {
    icon: Building,
    title: 'Perusahaan yang Akan Ekspansi',
    description: 'Yang butuh kesiapan tim untuk pertumbuhan bisnis'
  },
  {
    icon: Rocket,
    title: 'Organisasi Peningkatan Kapabilitas',
    description: 'Yang ingin meningkatkan kapabilitas manajerial dan operasional'
  },
  {
    icon: Users,
    title: 'HR Department',
    description: 'Yang butuh bantuan penyusunan struktur pelatihan berbasis strategi'
  },
  {
    icon: Landmark,
    title: 'Instansi Pemerintah & BUMN',
    description: 'Yang butuh pemetaan SDM dan pengembangan kapasitas'
  },
  {
    icon: UserPlus,
    title: 'Perusahaan dengan Kendala SDM',
    description: 'Yang sedang mengalami masalah dengan sumber daya manusia'
  }
];

const packages = [
  {
    icon: Building,
    title: 'Korporasi & Startup',
    description: 'Yang sedang scaling dan membutuhkan struktur SDM yang kuat'
  },
  {
    icon: Landmark,
    title: 'Instansi Pemerintah',
    description: 'Yang ingin memperkuat kapasitas birokrasi'
  },
  {
    icon: Users,
    title: 'Pembangunan Budaya Organisasi',
    description: 'Perusahaan yang ingin membangun budaya dan retensi SDM'
  },
  {
    icon: Target,
    title: 'Tim HR',
    description: 'Yang ingin solusi terstruktur dan berbasis data untuk pengembangan SDM'
  }
];

export function HumanResourcePlanningTraining() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: servicesRef, isInView: servicesInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-20 overflow-hidden"
      >
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/hr-planning-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">08</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Human Resource Planning & Training
            </h1>

            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              Kami membantu perusahaan <strong className="text-primary-400">menyusun rencana kebutuhan SDM</strong> serta <strong className="text-primary-400">mendesain program pelatihan berbasis kompetensi</strong> dan strategi bisnis jangka panjang.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Services & Suitable For Section */}
      <section ref={servicesRef} className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto section-padding">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Layanan Kami
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Solusi komprehensif untuk perencanaan dan pengembangan sumber daya manusia
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-white/60 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Suitable For Section */}
          <div className="glass rounded-2xl p-8 md:p-12">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-white mb-6 text-center">
              Cocok Untuk
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {suitableFor.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-4 p-4 bg-white/5 rounded-xl"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-white/60 text-sm">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/hr-packages-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="max-w-7xl mx-auto section-padding relative z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Paket Human Resource Planning & Training
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Sesuai untuk berbagai jenis organisasi dan kebutuhan
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-2xl p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <pkg.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{pkg.title}</h3>
                    <p className="text-white/60 text-sm">{pkg.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary-400" />
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Siap Mengembangkan Tim Anda?
            </h2>

            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Dapatkan solusi HR planning dan training yang terstruktur untuk membangun tim yang kuat dan kompeten.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
